import { defineComponent, onMounted, ref, reactive, watch, toRefs } from '@vue/composition-api';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';

/**
 * リアクティブな変数の型定義
 */
export type reactiveData = {
    editor?: Quill;             // Quill
    text: string;               // 入力テキスト
    counter: number;            // 最大文字数（カウンタとバリデーションに用いる）
    inputCounter: number;       // 入力中の文字数
    placeHolder: string;        // テキストが未入力の際のプレースホルダ（「〇〇〇で入力してください」等）
    message: string;            // バリデーションメッセージ
    readOnly: boolean;          // 表示専用モード
    isDarkTheme: boolean;       // ダークテーマ使用フラグ
};

/**
 * 引数
 */
interface Props {
    text: string,               // 入力テキスト
    rules: any,                 // バリデーション
    isError: boolean,           // バリデーションエラー有無
    counter: number,            // 最大文字数
    placeHolder: string,        // プレースホルダ
    readOnly: boolean           // 表示専用モード
}

/**
 * リッチテキストエディタコンポーネント
 */
export default defineComponent({
    props: {
        text: {
            default: '',
            type: String
        },                      // :text
        rules: {
            default: () => [],
            type: Array
        },                      // :rules
        rulesValid: {
            default: true,
            type: Boolean
        },                      // :rules-valid
        counter: Number,        // :counter
        placeHolder: String,    // :place-holder
        readOnly: {             // :readOnly
            default: false,
            type: Boolean
        }
    },
    setup(props: Props, context) {

        // リアクティブな変数の宣言
        const state: reactiveData = reactive<reactiveData>({
            editor: undefined,
            text: '',
            counter: 0,
            inputCounter: 0,
            placeHolder: '',
            message: '',
            readOnly: false,
            isDarkTheme: context.root.$vuetify.theme.dark
        });

        // storeのダークテーマ使用フラグを監視し、ダークテーマ使用フラグの変更を行う
        watch(context.root.$store.state.commonLayout, () => {
            state.isDarkTheme = context.root.$store.state.commonLayout.isDarkTheme;
        })

        // エディタ初期化
        const quillEditor = ref<HTMLDivElement>();
        let editor = ref<any>(state.editor);
        const { text } = toRefs(props);

        watch(text, () => {
            if (editor.root.innerHTML !== text.value) {
                editor.root.innerHTML = text.value;
            }
        })

        // ツールバー設定
        const toolbarOptionsDefault: any = [
            [{ 'size': ['small', false, 'large', 'huge'] }],                  // 文字サイズ
            ['bold', 'italic', 'underline', 'strike'],                        // 太字・斜体・下線・打ち消し線
            ['blockquote', 'code-block'],                                     // 引用・コード
            [{ 'list': 'bullet' }, { 'list': 'ordered' }],                    // リスト
            [{ 'indent': '-1' }, { 'indent': '+1' }],                         // インデント
            [{ 'align': '' }, { 'align': 'center' }, { 'align': 'right' }],   // 行開始位置
            [{ 'script': 'sub' }, { 'script': 'super' }],                     // 上付き文字・下付き文字
            [{ 'header': 1 }, { 'header': 2 }],                               // h1 または h2
            [{
                'color': [                                                    // 文字色
                    "#000000", "#e60000", "#ff9900", "#ffff00", "#008a00", "#0066cc", "#9933ff",
                    "#ffffff", "#facccc", "#ffebcc", "#ffffcc", "#cce8cc", "#cce0f5", "#ebd6ff",
                    "#bbbbbb", "#f06666", "#ffc266", "#ffff66", "#66b966", "#66a3e0", "#c285ff",
                    "#888888", "#a10000", "#b26b00", "#b2b200", "#006100", "#0047b2", "#6b24b2",
                    "#444444", "#5c0000", "#663d00", "#666600", "#003700", "#002966", "#3d1466"
                ]
            },
            {
                'background': [                                               // 背景色
                    "#000000", "#e60000", "#ff9900", "#ffff00", "#008a00", "#0066cc", "#9933ff",
                    "#ffffff", "#facccc", "#ffebcc", "#ffffcc", "#cce8cc", "#cce0f5", "#ebd6ff",
                    "#bbbbbb", "#f06666", "#ffc266", "#ffff66", "#66b966", "#66a3e0", "#c285ff",
                    "#888888", "#a10000", "#b26b00", "#b2b200", "#006100", "#0047b2", "#6b24b2",
                    "#444444", "#5c0000", "#663d00", "#666600", "#003700", "#002966", "#3d1466"
                ]
            }],
            ['link'],                                                         // 画像・リンク
            ['clean'],                                                        // 適用したスタイルを除去する
        ];

        onMounted(() => {
            // 入力中の文字数
            state.counter = props.counter;
            // 未入力の際のプレースホルダ
            state.placeHolder = props.placeHolder;
            // 表示専用モード
            state.readOnly = props.readOnly;

            // text-changeイベント
            Quill.register('modules/counter', function (quill: Quill) {
                quill.on('text-change', function () {
                    const text = quill.getText();
                    const replaced = text.replace(/\r?\n/g, '')
                    // 改行を削除した文字数
                    state.inputCounter = replaced.length;
                    // 改行を削除した文字でバリデーションを行う
                    validate(replaced);
                    // 親コンポーネントへ返す
                    notify(quill.root.innerHTML, replaced);
                });
            });

            if (!quillEditor.value) {
                return;
            }

            let toolbarOptions;
            // ツールバー表示有無
            if (props.readOnly) {
                toolbarOptions = false;
            } else {
                toolbarOptions = toolbarOptionsDefault;
            }

            editor = new Quill(quillEditor.value, {
                modules: {
                    toolbar: toolbarOptions,
                    counter: true
                },
                theme: 'snow',
                placeholder: state.placeHolder,
                readOnly: props.readOnly
            });
            state.editor = editor;
        })

        /**
         * バリデーション
         */
        function validate(value: string) {
            for (let index = 0; index < props.rules.length; index++) {
                const rule = props.rules[index];
                const valid = typeof rule === 'function' ? rule(value) : rule
                if (valid !== true) {
                    // エラーあり
                    state.message = valid;
                    // 親コンポーネントのrules-validにfalse設定
                    context.emit('update:rules-valid', false);
                    break;
                } else {
                    // エラーなし
                    state.message = '';
                    // 親コンポーネントのrules-validにtrueを設定
                    context.emit('update:rules-valid', true);
                }
            }
        }

        /**
         * 親コンポーネントへ文字が入力されたことを通知する
         * 
         * @param html HTML形式
         * @param text 改行除いた文字のみ（バリデーション用）
         */
        function notify(html: string, text: string,) {
            context.emit('textChange', html, text);
        }

        return {
            state,
            quillEditor
        };
    }
});
